import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ForgotPassword() {
  const [step, setStep] = useState(1); // 1: email, 2: temp password sent
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSendTempPassword = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error('Please enter your email or username');
      return;
    }

    setLoading(true);
    try {
      // Send as both email and username - backend will handle finding the user
      const response = await axios.post(`${API}/auth/forgot-password/send-temp`, {
        email: email.trim(),
        username: email.trim()
      });
      
      setStep(2);
      toast.success('Temporary password sent! Check your email.');
      
      if (response.data.warning) {
        toast.warning(response.data.warning);
      }
    } catch (error) {
      console.error('Error sending temp password:', error);
      toast.error(error.response?.data?.detail || 'Failed to send temporary password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #f8fafc 0%, #e0e7ff 50%, #f0f9ff 100%)',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Background gradient effects */}
      <div style={{
        position: 'absolute',
        top: '10%',
        left: '10%',
        width: '400px',
        height: '400px',
        background: 'rgba(59, 130, 246, 0.15)',
        borderRadius: '50%',
        filter: 'blur(100px)'
      }} />
      <div style={{
        position: 'absolute',
        bottom: '10%',
        right: '10%',
        width: '400px',
        height: '400px',
        background: 'rgba(139, 92, 246, 0.15)',
        borderRadius: '50%',
        filter: 'blur(100px)'
      }} />

      <div style={{
        width: '100%',
        maxWidth: '440px',
        padding: '40px',
        background: 'white',
        border: '1px solid #e2e8f0',
        borderRadius: '24px',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.1)',
        position: 'relative',
        zIndex: 1
      }}>
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{
            width: '56px',
            height: '56px',
            margin: '0 auto 16px',
            borderRadius: '16px',
            background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '24px',
            boxShadow: '0 8px 20px rgba(59, 130, 246, 0.3)'
          }}>
            🔑
          </div>
          <h1 style={{ 
            fontFamily: 'Outfit, sans-serif',
            fontSize: '28px', 
            fontWeight: '700', 
            color: '#0f172a',
            marginBottom: '8px' 
          }}>
            {step === 1 ? 'Reset Password' : 'Check Your Email'}
          </h1>
          <p style={{ fontSize: '14px', color: '#64748b' }}>
            {step === 1 
              ? "Enter your email and we'll send you a temporary password" 
              : "A temporary password has been sent to your email"
            }
          </p>
        </div>

        {/* Step 1: Email */}
        {step === 1 && (
          <form onSubmit={handleSendTempPassword}>
            <div className="form-group">
              <label className="form-label" style={{ color: '#334155' }}>Email or Username</label>
              <input
                type="text"
                className="form-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email or username"
                required
                data-testid="email-input"
                style={{
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  color: '#0f172a'
                }}
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              style={{ 
                width: '100%', 
                marginTop: '8px',
                background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                border: 'none',
                padding: '12px',
                borderRadius: '10px',
                fontWeight: '600',
                color: '#fff',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.6 : 1,
                boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
              }}
              data-testid="send-temp-btn"
            >
              {loading ? 'Sending...' : 'Send Temporary Password'}
            </Button>
          </form>
        )}

        {/* Step 2: Confirmation */}
        {step === 2 && (
          <div>
            <div style={{ 
              padding: '20px', 
              background: '#f0fdf4', 
              border: '1px solid #bbf7d0', 
              borderRadius: '12px',
              textAlign: 'center',
              marginBottom: '24px'
            }}>
              <div style={{ fontSize: '48px', marginBottom: '12px' }}>✉️</div>
              <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#16a34a', marginBottom: '8px' }}>
                Email Sent!
              </h3>
              <p style={{ fontSize: '14px', color: '#64748b' }}>
                Check your inbox for the temporary password. Use it to log in, and you'll be prompted to set a new password.
              </p>
            </div>

            <Button
              onClick={() => navigate('/login')}
              style={{ 
                width: '100%',
                background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                border: 'none',
                padding: '12px',
                borderRadius: '10px',
                fontWeight: '600',
                color: '#fff',
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
              }}
              data-testid="go-to-login-btn"
            >
              Go to Login
            </Button>

            <Button
              onClick={() => setStep(1)}
              style={{ 
                width: '100%',
                marginTop: '12px',
                background: '#f1f5f9',
                border: '1px solid #e2e8f0',
                padding: '12px',
                borderRadius: '10px',
                fontWeight: '600',
                color: '#475569',
                cursor: 'pointer'
              }}
            >
              Didn't receive it? Try again
            </Button>
          </div>
        )}

        <div style={{ marginTop: '24px', textAlign: 'center', fontSize: '14px', color: '#64748b' }}>
          Remember your password?{' '}
          <Link to="/login" style={{ color: '#3b82f6', fontWeight: '600', textDecoration: 'none' }}>
            Sign in
          </Link>
        </div>
      </div>
    </div>
  );
}

export default ForgotPassword;
